import {
  Button,
  DesignLanguage,
  Grid,
  GutterVariantTypes,
  Input,
  InputEmailPhone,
  Link,
  Message,
  Text,
  Theme,
  useDesignLanguagePerClass,
  useIsDesignLanguage,
  usePhoneNumber,
  useTheme,
  useThemeClass,
} from '@afterpaytouch/core'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { ChangeEvent, createRef, FormEvent, FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { AppLink } from '../AppLink'
import { DisabledModal } from '../DisabledModal'
import { Form } from '../Form'
import { ProfileCard } from '../ProfileCard'
import { Trans } from '../../utils/convergence'
import { Route } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import {
  AuthenticationErrors,
  AuthLoginRequest,
  AuthLoginResponse,
  BiometricErrorNameTypes,
  BiometricLoginStatus,
  ConsumerAccountResponse,
  isGenericApiError,
  LoginMethodTypes,
} from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import {
  accountLookup,
  authLogin,
  useAppDispatch,
  useAuthenticationErrors,
  useConsumerSelector,
  useCountry,
  useMobileLogin,
  useBiometric,
  biometricLogin,
  setBiometricLoginStatus,
  useLogLoginAttemptEvent,
  setHasSilentBiometricErrors,
  isCredentialPresent,
  useIsEligibleBiometricLogin,
  useShouldAutoTriggerBiometrics,
  useIsApplePaylater,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'

import { SILENT_BIOMETRIC_ERROR_MESSAGES } from './constants'
import styles from './styles.module.css'
import { getLoginPasswordErrorMessage, getUsername, LOGIN_PASSWORD_ERROR_MESSAGES, LoginPasswordErrorMessage } from './utils'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { DDPrivacyLevel } from '@afterpaytouch/integrations/datadog'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { useTranslation } from '../../utils/convergence'
import { BiometricButton, ForgotPasswordLink, NeedHelpLink, OTPButton } from './components'
import clsx from 'clsx'
import { getAnonymousErrorPayload } from '../../utils/logging'

interface Props {
  onSuccess: (account: ConsumerAccountResponse) => void
  onOTP: () => void
  onRequires2FA: () => void
  onDeclined: () => void
  onForcePasswordResetRequired: () => void
}

export const LoginPassword: FunctionComponent<Props> = ({ onSuccess, onOTP, onRequires2FA, onDeclined, onForcePasswordResetRequired }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const country = useCountry()
  const { formatInternational } = usePhoneNumber({ country })
  const { email = '', mobile = '' } = useConsumerSelector()
  const { isMobileLogin } = useMobileLogin()
  const { error } = useAuthenticationErrors()
  const [formData, setFormData] = useState<AuthLoginRequest>({
    username: getUsername(isMobileLogin, mobile, email),
    password: '',
  })

  const { loginStatus: biometricLoginStatus } = useBiometric()
  const isBiometricLoggingIn = biometricLoginStatus === BiometricLoginStatus.Started || biometricLoginStatus === BiometricLoginStatus.Finished
  const credentialPresent = isCredentialPresent({ email, mobile })
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const [errorMessage, setErrorMessage] = useState<LoginPasswordErrorMessage>()
  const [submitted, setSubmitted] = useState(isBiometricLoggingIn)
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const isApplePaylaterFlow = useIsApplePaylater()

  const [buttonsEnabled, setButtonsEnabled] = useState(!isApplePaylaterFlow)
  const [otpButtonLoading, setOtpButtonLoading] = useState(false)
  const [passwordNotRevealedOnce, setPasswordNotRevealedOnce] = useState(true)
  const inputRef = createRef<HTMLInputElement>()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { buttonSize, textSize } = useCheckoutSizes()

  const shouldAutoTriggerBiometric = useShouldAutoTriggerBiometrics()
  const isEligibleBiometricLogin = useIsEligibleBiometricLogin()
  const themeClass = useThemeClass()
  const designLanguageClasses = useDesignLanguagePerClass(styles)
  const {
    theme: { selected: currentTheme },
  } = useTheme()

  const logLoginAttemptEvent = useLogLoginAttemptEvent({ is2fa: false })

  const handleFieldChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [ev.target.name]: ev.target.value,
    })
  }

  const handleLoginError = (e: unknown): void => {
    const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
    if (errorCode === ErrorCode.AuthenticationFailure) {
      logEvent(TrackingEvent.AUTHENTICATION_FAILURE)
    } else if (errorCode === ErrorCode.AuthenticationFailureMaxAttempts) {
      logEvent(TrackingEvent.AUTHENTICATION_FAILURE_LOCKOUT)
      setShowErrorMessage(true)
    } else if (errorCode === ErrorCode.PaywatchCheckFailed) {
      logEvent(TrackingEvent.CHECKOUT_LOGIN_PAYWATCH_FAILED)
    } else if (errorCode === ErrorCode.AuthenticationDeclined) {
      onDeclined()
      setSubmitted(false)
      return
    } else if (errorCode === ErrorCode.ForcePasswordResetRequired) {
      onForcePasswordResetRequired()
      setSubmitted(false)
      return
    } else if (isGenericApiError(e)) {
      logEvent(TrackingEvent.VIEWED_ANONYMOUS_USER_LOGIN_ERROR, getAnonymousErrorPayload(e))
    }
    setErrorMessage(getLoginPasswordErrorMessage(errorCode, isApplePaylaterFlow))
    setSubmitted(false)
  }

  const handleLogin = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    setSubmitted(true)
    setErrorMessage(undefined)

    if (formData.password === '') {
      setErrorMessage(LOGIN_PASSWORD_ERROR_MESSAGES.EmptyPassword)
      setSubmitted(false)
      inputRef.current?.focus()
      return
    }
    logEvent(TrackingEvent.PRESSED_LOGIN_PASSWORD_CONTINUE)

    const loginMethod: LoginMethodTypes = 'Web Password'
    try {
      const result: AuthLoginResponse = unwrapResult(await dispatch(authLogin(formData)))
      if (result.user.requires2fa) {
        onRequires2FA()
      } else {
        const response = unwrapResult(await dispatch(accountLookup()))
        if (isUndefinedOrEmpty(response.email) && isUndefinedOrEmpty(response.phoneNumber)) {
          logEvent(TrackingEvent.ACCOUNT_LOOKUP_RESPONSE_EMPTY)
        }
        onSuccess(response)

        logLoginAttemptEvent(loginMethod, true)
      }
    } catch (e) {
      handleLoginError(e)
      logLoginAttemptEvent(loginMethod, false)
    }
  }

  const startBiometricLogin = async (): Promise<void> => {
    try {
      await dispatch(biometricLogin(email, mobile, isMobileLogin))

      const account = await dispatch(accountLookup()).unwrap()
      onSuccess(account)
      logEvent(TrackingEvent.CHECKOUT_BIOMETRIC_LOGIN_SUCCESSFUL, { isBiometricLogin: true })
      logLoginAttemptEvent('Biometric Login', true)
    } catch (e) {
      handleBiometricError(e)
    }
  }

  const handleBiometricError = (e): void => {
    if (isPortalApiError(e)) {
      handleLoginError(e)
    }

    // handle biometric errors that should be kept silent to the user
    if (BiometricErrorNameTypes.includes(e?.name) || SILENT_BIOMETRIC_ERROR_MESSAGES.includes(e?.message)) {
      dispatch(setBiometricLoginStatus(BiometricLoginStatus.Error))
      if (SILENT_BIOMETRIC_ERROR_MESSAGES.includes(e?.message)) {
        dispatch(setHasSilentBiometricErrors(true))
      }
    } else {
      capture.error(e)
    }

    if (!SILENT_BIOMETRIC_ERROR_MESSAGES.includes(e?.message)) {
      logLoginAttemptEvent('Biometric Login', false)
    }

    logEvent(TrackingEvent.CHECKOUT_BIOMETRIC_LOGIN_FAILED, { error: e?.name ?? e?.message, errorDetails: e?.message, isBiometricLogin: true })
  }

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    setSubmitted(isBiometricLoggingIn)
  }, [biometricLoginStatus])

  useEffect(() => {
    if (error === AuthenticationErrors.AccountLocked) {
      setShowErrorMessage(true)
      setButtonsEnabled(false)
      setErrorMessage(LOGIN_PASSWORD_ERROR_MESSAGES.AccountLocked)
    }
  }, [error])

  useEffect(() => {
    if (shouldAutoTriggerBiometric) {
      startBiometricLogin()
    }
  }, [shouldAutoTriggerBiometric])

  useEffect(() => {
    if (credentialPresent) {
      logEvent(TrackingEvent.BIOMETRIC_LOGIN_LOCAL_STORAGE_PRESENT, { isEligibleBiometricLogin })
    }
  }, [credentialPresent, isEligibleBiometricLogin])

  useEffect(() => {
    logEvent(TrackingEvent.BIOMETRIC_LOGIN_ELIGIBILITY, {
      result: isEligibleBiometricLogin,
    })
  }, [isEligibleBiometricLogin])

  const getErrorHeading = (errorKey: LoginPasswordErrorMessage | undefined): string | undefined => {
    switch (errorKey) {
      case LOGIN_PASSWORD_ERROR_MESSAGES.Auth:
        return t('password:error.auth.heading')
      case LOGIN_PASSWORD_ERROR_MESSAGES.AuthMaxAttempt:
        return t('password:error.authMaxAttempt.heading')
      case LOGIN_PASSWORD_ERROR_MESSAGES.AuthMaxAttemptApplePaylater:
        return t('password:error.authMaxAttempt_applepaylater.heading')
      case LOGIN_PASSWORD_ERROR_MESSAGES.AccountDisabled:
        return t('password:error.accountDisabled.heading')
      case LOGIN_PASSWORD_ERROR_MESSAGES.Locked:
        return t('password:error.locked.heading')
      case LOGIN_PASSWORD_ERROR_MESSAGES.PaywatchCheckFailed:
        return t('password:error.paywatchCheckFailed.heading')
      case LOGIN_PASSWORD_ERROR_MESSAGES.AccountLocked:
        return t('password:error.accountLocked.heading')
      case LOGIN_PASSWORD_ERROR_MESSAGES.Unknown:
        return t('password:error.unknown.heading')
      case LOGIN_PASSWORD_ERROR_MESSAGES.EmptyPassword:
      default:
        return undefined
    }
  }

  const errorComponents = {
    help: <Link key='description-link' target='_blank' kind='Underline' href={t('common:helpUrl')} />,
    helpLanding: <Link key='description-link' target='_blank' kind='Underline' href={t('common:helpLandingUrl')} />,
    helpPaywatch: <Link key='description-link' target='_blank' kind='Underline' href={t('common:paywatchHelpUrl')} />,
    paywatchDesc: <span className={styles.paywatchDescription} />,
    reset: <AppLink noMargin inline route={Route.VERIFY} />,
    otp: <AppLink noMargin inline route={Route.ONE_TIME_PASSWORD} />,
  }

  const renderError = (errorKey: LoginPasswordErrorMessage | undefined): ReactNode => {
    switch (errorKey) {
      case LOGIN_PASSWORD_ERROR_MESSAGES.EmptyPassword:
        return <Trans i18nKey='password:error:emptyPassword' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.Auth:
        return <Trans i18nKey='password:error.auth' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.AuthMaxAttempt:
        return <Trans i18nKey='password:error.authMaxAttempt' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.AuthMaxAttemptApplePaylater:
        return <Trans i18nKey='password:error.authMaxAttempt_applepaylater' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.AccountDisabled:
        return <Trans i18nKey='password:error.accountDisabled' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.Locked:
        return <Trans i18nKey='password:error.locked' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.PaywatchCheckFailed:
        return <Trans i18nKey='password:error.paywatchCheckFailed' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.AccountLocked:
        return <Trans i18nKey='password:error.accountLocked' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.Unknown:
        return <Trans i18nKey='password:error.unknown' components={errorComponents} />
      default:
        return <Trans i18nKey='' components={errorComponents} />
    }
  }

  const renderErrorDescription = (errorKey: LoginPasswordErrorMessage | undefined): ReactNode => {
    switch (errorKey) {
      case LOGIN_PASSWORD_ERROR_MESSAGES.EmptyPassword:
        return <Trans i18nKey='password:error:emptyPassword' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.Auth:
        return <Trans i18nKey='password:error.auth.description' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.AuthMaxAttempt:
        return <Trans i18nKey='password:error.authMaxAttempt.description' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.AuthMaxAttemptApplePaylater:
        return <Trans i18nKey='password:error.authMaxAttempt_applepaylater.description' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.AccountDisabled:
        return <Trans i18nKey='password:error.accountDisabled.description' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.Locked:
        return <Trans i18nKey='password:error.locked.description' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.PaywatchCheckFailed:
        return <Trans i18nKey='password:error.paywatchCheckFailed.description' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.AccountLocked:
        return <Trans i18nKey='password:error.accountLocked.description' components={errorComponents} />
      case LOGIN_PASSWORD_ERROR_MESSAGES.Unknown:
        return <Trans i18nKey='password:error.unknown.description' components={errorComponents} />
      default:
        return null
    }
  }

  const handlePasswordMask = (isMasked: boolean): void => {
    if (passwordNotRevealedOnce && !isMasked) {
      logEvent(TrackingEvent.TOGGLE_REVEAL_PASSWORD)

      setPasswordNotRevealedOnce(false)
    }
  }

  const handleOtpButtonClick = (): void => {
    setOtpButtonLoading(true)
    onOTP()
  }

  const handleBiometricButtonClick = (): void => {
    startBiometricLogin()
    logEvent(TrackingEvent.PRESSED_EXPLICIT_LOGIN_PASSWORD_PAGE_BIOMETRIC_LOGIN)
  }

  const mapThemeToButtonGap: Record<Theme, GutterVariantTypes> = {
    [Theme.AP]: 2,
    [Theme.Cash]: 3,
    [Theme.Pegasus]: 2,
    [Theme.Kelpie]: 3,
    [Theme.KelpieMint]: 3,
  }

  const forgetPasswordAndLogginLinksClassname = clsx(styles.forgetPasswordAndLogginLinks, styles[themeClass])

  const renderButtons = (): ReactNode => {
    return (
      <>
        <BiometricButton onClick={handleBiometricButtonClick} submitted={submitted} />
        <OTPButton loading={otpButtonLoading} onClick={handleOtpButtonClick} disabled={submitted} />
        <Grid>
          <div className={forgetPasswordAndLogginLinksClassname}>
            <Text renderAs='div'>
              {isMobileLogin ? (
                <Trans
                  i18nKey={'password:forgotPasswordAndNeedHelpLinks'}
                  components={{
                    forgotLink: <ForgotPasswordLink />,
                    helpLink: <NeedHelpLink />,
                  }}
                />
              ) : (
                <ForgotPasswordLink />
              )}
            </Text>
          </div>
        </Grid>
      </>
    )
  }

  return (
    <>
      <div className={designLanguageClasses.loginPasswordForm}>
        <Form onSubmit={handleLogin}>
          <ProfileCard allowLogout revealMobile header={isMobileLogin ? formatInternational(formData.username) : formData.username} />
          {showErrorMessage ? (
            <>
              <Grid>
                <Grid.Item>
                  <Message
                    kind='Error'
                    iconName={isArcadeDesignLanguage ? 'AlertReportedFill' : 'Profile'}
                    heading={!isArcadeDesignLanguage ? getErrorHeading(errorMessage) : undefined}
                    description={<Text size='XS'>{renderErrorDescription(errorMessage)}</Text>}
                    testNameSpace='login-error'
                  />
                </Grid.Item>
              </Grid>
              <Grid verticalGap={mapThemeToButtonGap[currentTheme]}>{buttonsEnabled && renderButtons()}</Grid>
            </>
          ) : (
            <>
              {!isArcadeDesignLanguage && (
                <Grid>
                  <Text size={textSize} id='loginPasswordLabel'>
                    {t('password:enterPassword')}
                  </Text>
                </Grid>
              )}
              <Grid center>
                <InputEmailPhone type='hidden' value={formData.username} autoComplete='username' name='username' testNameSpace='login-username-hidden' />
                <Grid.Item>
                  <MaskedElement stylingModifier='wrap' privacyLevel={DDPrivacyLevel.Hidden} actionName='Login Password Input'>
                    <Input.Password
                      label={isArcadeDesignLanguage ? t('password:enterPassword') : t('common:password')}
                      testNameSpace='login-password'
                      onPasswordMaskChange={handlePasswordMask}
                      onChange={handleFieldChange}
                      name='password'
                      disabled={submitted}
                      autoComplete='current-password'
                      error={Boolean(errorMessage)}
                      errorText={renderError(errorMessage)}
                      ref={inputRef}
                      aria-labelledby='loginPasswordLabel'
                    />
                  </MaskedElement>
                </Grid.Item>
              </Grid>

              <Grid verticalGap={mapThemeToButtonGap[currentTheme]}>
                <Grid.Item>
                  <Button.Primary loading={submitted} size={buttonSize} type='submit' testNameSpace='login-password' disabled={submitted} padding='Fluid'>
                    {t('common:continue')}
                  </Button.Primary>
                </Grid.Item>
                {buttonsEnabled && renderButtons()}
              </Grid>
            </>
          )}
        </Form>
      </div>
      {submitted && <DisabledModal />}
    </>
  )
}
